import { ApolloError, ServerError, ServerParseError } from '@apollo/client';
import { GraphQLFormattedError } from 'graphql';

// HotChocolate returns auth / pincode failures with an `extensions.code` of
// "401" / "428" AND an HTTP status of 401 / 428 (see CustomHttpResponseFormatter
// on the server). Depending on the response content-type, Apollo surfaces these
// as either a graphQLError or a networkError (ServerError) — so we collect codes
// from every place they can appear.

export const PINCODE_TIMEOUT_CODE = '428';
export const UNAUTHORIZED_CODE = '401';

export function getErrorCodes(
  graphQLErrors?: readonly GraphQLFormattedError[],
  networkError?: ApolloError['networkError']
): string[] {
  const codes: string[] = [];

  graphQLErrors?.forEach(error => {
    const code = error.extensions?.code;
    if (code != null) {
      codes.push(code.toString());
    }
  });

  if (networkError) {
    const {statusCode} = (networkError as ServerError | ServerParseError);
    if (statusCode != null) {
      codes.push(statusCode.toString());
    }

    const {result} = (networkError as ServerError);
    if (result && typeof result === 'object' && 'errors' in result) {
      (result.errors as GraphQLFormattedError[] | undefined)?.forEach(error => {
        const code = error.extensions?.code;
        if (code != null) {
          codes.push(code.toString());
        }
      });
    }
  }

  return codes;
}

export function getApolloErrorCodes(error?: ApolloError | null): string[] {
  if (!error) {
    return [];
  }
  return getErrorCodes(error.graphQLErrors, error.networkError);
}

/** Pincode/idle lock (428) — the user must re-enter their pincode. */
export function isPinCodeError(codes: string[]): boolean {
  return codes.includes(PINCODE_TIMEOUT_CODE);
}

/** Auth failure (401) — token expired / unauthenticated. */
export function isUnauthorizedError(codes: string[]): boolean {
  return codes.includes(UNAUTHORIZED_CODE);
}

/**
 * A ValidateInputException from the server: `extensions.code` names the
 * exception, `extensions.validation` the rule (the server enum name, e.g.
 * "AlreadyExists") and `extensions.target`, when set, which item it is about
 * (e.g. the id of a person in a registration).
 */
export type InputValidationError = {
  validation: string;
  target?: string;
};

export function getInputValidation(
  error: unknown
): InputValidationError | undefined {
  if (!(error instanceof ApolloError)) {
    return undefined;
  }

  const found = error.graphQLErrors.find(
    graphQLError => graphQLError.extensions?.code === 'ValidateInputException'
  );
  if (!found) {
    return undefined;
  }

  return {
    validation: String(found.extensions?.validation ?? ''),
    target:
      found.extensions?.target != null
        ? String(found.extensions.target)
        : undefined,
  };
}

export function hasInputValidation(error: unknown, validation: string): boolean {
  return getInputValidation(error)?.validation === validation;
}
